import { useDistanceUnit } from "@/components/common/settings-context/settings-context";
import { ElementIcon, ElementIconType } from "@/components/ui/icons";
import { ReactComponent as PanoPreviewSvg } from "@/components/ui/icons/pano-view.svg";
import { Pano360PreviewCanvas } from "@/components/ui/pano-360-preview-canvas";
import { getCoordinateStringZUp } from "@/components/ui/scene-context-menu";
import { PanoObject } from "@/object-cache";
import {
  FaroPopover,
  FaroSvgIcon,
  FaroText,
  grey,
  NO_TRANSLATE_CLASS,
  WorldCoordinateIcon,
} from "@faro-lotv/flat-ui";
import {
  convertToDateString,
  isLoading,
  Loading,
  SignedUrl,
} from "@faro-lotv/foundation";
import {
  IElementTypeHint,
  ISOTimeString,
  URI,
} from "@faro-lotv/ielement-types";
import { useValidUrl } from "@faro-lotv/project-source";
import {
  CardMedia,
  Fade,
  PopperProps,
  Skeleton,
  useTheme,
} from "@mui/material";
import { Box, Stack } from "@mui/system";
import { useMemo, useState, type JSX } from "react";
import { Vector3Tuple } from "three";

/** The height of the preview image in px */
const PREVIEW_IMAGE_HEIGHT = 180;

export type PlaceholderPreviewBaseProps = Pick<
  PopperProps,
  "anchorEl" | "placement" | "disablePortal"
> & {
  /** The name of the placeholder. */
  name?: string;

  /** The URL to the image to render. */
  imageUri?: URI | SignedUrl | null | Loading;

  /** The pano object to render. */
  panoObject?: PanoObject | null;

  /** The reason why the scan is disabled */
  disabledReason?: string;

  /** ISO 8601 timestamp when the element has been created. */
  createdAt?: ISOTimeString;

  /** Whether the preview should be visible */
  isVisible?: boolean;

  /**
   * Called when the pointer enters or leaves the preview popover itself.
   * Allows the parent to keep the preview open while the user hovers over it.
   */
  onPreviewHoverChange?(isHovered: boolean): void;

  /** The name of the dataset the placeholder belongs to. */
  datasetName?: string;

  /** The type of the dataset the placeholder belongs to. */
  datasetType?: IElementTypeHint;

  /** The coordinates (y-up) of the placeholder. */
  coordinates?: Vector3Tuple;

  /** Additional modifiers to add to the popper component. */
  popperModifiers?: PopperProps["modifiers"];
};

/**
 * @returns  A dialog that displays a preview of an Img360 placeholder on hover,
 * while handling warm-up and cool-down transitions. The warm-up transition
 * introduces a delay before showing the preview window upon hovering,
 * ensuring a smooth and deliberate interaction. The cool-down transition
 * introduces a delay before hiding the preview window after moving the
 * pointer away, preventing flickering or abrupt disappearance.
 *
 * These transitions contribute to a better user experience by providing a
 * subtle delay for previewing and dismissing the content, reducing visual
 * noise and creating a more polished interaction.
 */
export function PlaceholderPreviewBase({
  name,
  imageUri,
  panoObject,
  disabledReason,
  createdAt,
  isVisible,
  onPreviewHoverChange,
  anchorEl,
  datasetName,
  datasetType,
  coordinates,
  popperModifiers,
  disablePortal = true,
  placement = "bottom",
}: PlaceholderPreviewBaseProps): JSX.Element | null {
  const theme = useTheme();
  const hasPanoramaMedia = !!panoObject;
  /**
   * The duration used for both fade in/out of window as well as the warm up and cool down time to make the window visible/invisible
   * i.e handles the delay between pointer hover and the popup visibility both while hovering in (warm-up) and hovering out (cool-down)
   */
  const transitionDuration = theme.transitions.duration.shorter;

  const [isPreviewLoading, setIsPreviewImageLoading] = useState(true);

  const isImageLoading =
    !hasPanoramaMedia &&
    (isLoading(imageUri) || (isPreviewLoading && !!imageUri));
  const cardImage =
    hasPanoramaMedia || isLoading(imageUri)
      ? undefined
      : (imageUri ?? undefined);
  const signedCardImage = useValidUrl(cardImage);

  const distanceUnit = useDistanceUnit();
  const formattedCoordinates = useMemo(() => {
    if (coordinates) {
      return getCoordinateStringZUp(coordinates, distanceUnit, true);
    }
  }, [coordinates, distanceUnit]);

  return (
    <Fade in={isVisible} timeout={transitionDuration}>
      {/** This div is needed to fix an issue with Fade: https://github.com/mui/material-ui/issues/27154#issuecomment-1117386458 */}
      <Box component="div">
        <FaroPopover
          open
          dark
          disablePortal={disablePortal}
          placement={placement}
          anchorEl={anchorEl}
          showCloseButton={false}
          sx={{
            // Remove default padding around content and make it fit the content
            " .MuiPaper-root": {
              // Remove padding on top, between content and arrow
              "> .MuiStack-root": {
                gap: 0,
              },
              p: 0,
              maxWidth: "fit-content",
            },
            // Have the arrow pass events through to the popover content.
            "& .MuiPopper-arrow": {
              pointerEvents: "none",
            },
          }}
          modifiers={popperModifiers}
        >
          <Box
            component="div"
            onPointerEnter={
              hasPanoramaMedia ? () => onPreviewHoverChange?.(true) : undefined
            }
            onPointerLeave={
              hasPanoramaMedia ? () => onPreviewHoverChange?.(false) : undefined
            }
            // The preview is rendered through a React portal, so events still
            // bubble through the React tree to ancestors. Stop pointer/click
            // events here so interacting with the preview doesn't trigger
            // selection or other side effects.
            onPointerDown={(ev) => ev.stopPropagation()}
            onMouseDown={(ev) => ev.stopPropagation()}
            onClick={(ev) => ev.stopPropagation()}
            sx={{
              width: 400,
              background: grey[950],
              padding: "12px",
              borderRadius: "4px",
            }}
          >
            <Stack
              direction="row"
              sx={{
                justifyContent: "space-between",
                alignItems: "center",
                mb: "10px",
              }}
            >
              <FaroText
                className={NO_TRANSLATE_CLASS}
                variant="labelM"
                color={grey[100]}
              >
                {name}
              </FaroText>
              <FaroText
                className={NO_TRANSLATE_CLASS}
                variant="bodyXS"
                color={grey[100]}
              >
                {createdAt ? convertToDateString(createdAt) : undefined}
              </FaroText>
            </Stack>

            {isImageLoading && (
              <Skeleton
                variant="rectangular"
                width="100%"
                height={PREVIEW_IMAGE_HEIGHT}
                sx={{ background: grey[800] }}
              />
            )}

            {hasPanoramaMedia ? (
              <Box component="div" sx={{ position: "relative" }}>
                <Pano360PreviewCanvas panoObject={panoObject} />
                <Box
                  component="div"
                  sx={{
                    position: "absolute",
                    bottom: 4,
                    right: 8,
                    // so it doesn't block panorama interaction
                    pointerEvents: "none",
                  }}
                >
                  <FaroSvgIcon
                    source={PanoPreviewSvg}
                    aria-label="pano preview icon"
                    fontSize="large"
                  />
                </Box>
              </Box>
            ) : (
              <CardMedia
                component="img"
                onLoad={() => setIsPreviewImageLoading(false)}
                onError={() => setIsPreviewImageLoading(false)}
                image={signedCardImage}
                sx={{
                  width: "100%",
                  height: PREVIEW_IMAGE_HEIGHT,
                  objectFit: "fill",
                  display: !isImageLoading && !!imageUri ? "block" : "none",
                }}
              />
            )}
            {disabledReason && !isImageLoading && (
              <FaroText variant="bodyS" dark>
                {disabledReason}
              </FaroText>
            )}
            <PreviewBottomInfo
              datasetName={datasetName}
              datasetType={datasetType}
              coordinates={formattedCoordinates}
            />
          </Box>
        </FaroPopover>
      </Box>
    </Fade>
  );
}

type PreviewBottomInfoProps = Pick<
  PlaceholderPreviewBaseProps,
  "datasetName" | "datasetType"
> & {
  /** The formatted coordinates of the pano */
  coordinates?: string;
};

function PreviewBottomInfo({
  datasetName,
  datasetType,
  coordinates,
}: PreviewBottomInfoProps): JSX.Element | null {
  if (!datasetName && !coordinates) return null;

  const icon =
    datasetType === IElementTypeHint.dataSetVideoWalk
      ? ElementIconType.DataSessionVideoRecording
      : ElementIconType.PointCloudIcon;

  return (
    <Stack direction="row" sx={{ alignItems: "flex-start", mt: "10px" }}>
      <Stack
        direction="row"
        sx={{ gap: 0.5, alignItems: "flex-start", width: "50%" }}
      >
        {datasetName && (
          <ElementIcon
            icon={icon}
            sx={{ color: grey[100], fontSize: "14px" }}
          />
        )}
        <FaroText
          className={NO_TRANSLATE_CLASS}
          variant="bodyXS"
          color={grey[100]}
        >
          {datasetName}
        </FaroText>
      </Stack>
      <Stack
        direction="row"
        sx={{
          gap: 0.5,
          alignItems: "flex-start",
          width: "50%",
          justifyContent: "flex-end",
        }}
      >
        {coordinates && (
          <WorldCoordinateIcon sx={{ color: grey[100], fontSize: "14px" }} />
        )}
        <FaroText
          className={NO_TRANSLATE_CLASS}
          variant="bodyXS"
          color={grey[100]}
        >
          {coordinates}
        </FaroText>
      </Stack>
    </Stack>
  );
}
